import React from 'react';
import { Box, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { GraphRenderer } from '../../Common/GraphRenderer';
import { RectangularPrism } from '../../Common/RectangularPrism';
import { Cube } from '../../Common/Cube';
import { Sphere } from '../../Common/Sphere';
import { Cylinder } from '../../Common/Cylinder';
import { Cone } from '../../Common/Cone';
import { Pyramid } from '../../Common/Pyramid';
import { TriangularPrism } from '../../Common/TriangularPrism';
import { Hemisphere } from '../../Common/Hemisphere';
import { Square } from '../../Common/Square';
import { Rectangle } from '../../Common/Rectangle';
import { Triangle } from '../../Common/Triangle';
import { Cube2D } from '../../Common/Cube2D';
import CoordinatePlane from '../../Common/CoordinatePlane';
import { isAnswerObject, type Question } from './mathWordProblemsTypes';

function QuestionVisualization({ question }: { question: Question }) {
  const vizType = question.visualizationType;
  const dims = question.dimensions || (question.graph?.config as { shape3DConfig?: { dimensions?: Record<string, number> } })?.shape3DConfig?.dimensions;
  const shapeConfig = (question.graph?.config as { shape3DConfig?: { unit?: string; color?: string } })?.shape3DConfig;
  const unit = shapeConfig?.unit || 'cm';
  const color = shapeConfig?.color;

  switch (vizType) {
    case 'rectangular_prism':
      return (
        <RectangularPrism
          length={dims?.length || 10}
          width={dims?.width || 6}
          height={dims?.height || 4}
          unit={unit}
          color={color}
        />
      );
    case 'cube':
      return <Cube side={dims?.side || 4} unit={unit} color={color} />;
    case 'sphere':
      return <Sphere radius={dims?.radius || 3} unit={unit} color={color} />;
    case 'cylinder':
      return <Cylinder radius={dims?.radius || 2} height={dims?.height || 5} unit={unit} color={color} />;
    case 'cone':
      return (
        <Cone
          radius={dims?.radius ?? 3}
          height={dims?.height}
          slantHeight={dims?.slantHeight}
          unit={unit}
          color={color}
        />
      );
    case 'pyramid':
      return (
        <Pyramid
          base={dims?.base ?? 4}
          height={dims?.height}
          slantHeight={dims?.slantHeight}
          unit={unit}
          color={color}
        />
      );
    case 'triangular_prism':
      return (
        <TriangularPrism
          base={dims?.base || 4}
          height={dims?.height || 3}
          length={dims?.length || 6}
          unit={unit}
          color={color}
        />
      );
    case 'hemisphere':
      return <Hemisphere radius={dims?.radius || 3} unit={unit} color={color} />;
    case 'square':
      return <Square side={dims?.side || 4} unit={unit} color={color} strokeColor={color} />;
    case 'rectangle':
      return (
        <Rectangle
          length={dims?.length || 8}
          width={dims?.width || 5}
          unit={unit}
          color={color}
          strokeColor={color}
        />
      );
    case 'triangle': {
      const triangleConfig = (question.graph?.config as { shape3DConfig?: Record<string, unknown> })?.shape3DConfig || {};
      const triangleType =
        (triangleConfig.triangleType as 'equilateral' | 'isosceles' | 'right' | 'scalene' | undefined) || 'scalene';
      const withAngles = Boolean(triangleConfig.withAngles);
      const angles = triangleConfig.angles as
        | { angle1?: number; angle2?: number; angle3?: number }
        | undefined;
      const showHypotenuse = Boolean(triangleConfig.showHypotenuse);
      const showSideLabels = triangleConfig.showSideLabels !== false;
      return (
        <Triangle
          base={dims?.base || 6}
          height={dims?.height || 5}
          side={dims?.side}
          hypotenuse={dims?.hypotenuse}
          unit={unit}
          triangleType={triangleType}
          color={color}
          strokeColor={color}
          withAngles={withAngles}
          angles={angles}
          showHypotenuse={showHypotenuse}
          showSideLabels={showSideLabels}
        />
      );
    }
    case 'cube2d':
      return <Cube2D side={dims?.side || 4} unit={unit} color={color} strokeColor={color} />;
    case 'coordinate_plane': {
      const graphConfig = question.graph?.config || {};
      const graphData = question.graph?.data || [];
      const coordConfig = graphConfig as Record<string, unknown>;

      const points = graphData.map((item: Record<string, unknown>, pointIndex: number) => {
        const x = typeof item.x === 'number' ? item.x : Number(item.x ?? 0);
        const y = typeof item.y === 'number' ? item.y : Number(item.y ?? 0);
        return {
          label: (item.label || item.name || String.fromCharCode(65 + pointIndex)) as string,
          x: Number.isFinite(x) ? x : 0,
          y: Number.isFinite(y) ? y : 0,
          color: item.color as string | undefined,
        };
      });

      let gridSize = { minX: 0, maxX: 10, minY: 0, maxY: 10 };
      const xDomain = (graphConfig as { xDomain?: [number, number] }).xDomain;
      const yDomain = (graphConfig as { yDomain?: [number, number] }).yDomain;

      if (xDomain && yDomain) {
        gridSize = {
          minX: xDomain[0],
          maxX: xDomain[1],
          minY: yDomain[0],
          maxY: yDomain[1],
        };
      } else if (points.length > 0) {
        const pad = 1;
        const xValues = points.map((p) => p.x);
        const yValues = points.map((p) => p.y);
        gridSize = {
          minX: Math.min(0, ...xValues) - pad,
          maxX: Math.max(0, ...xValues) + pad,
          minY: Math.min(0, ...yValues) - pad,
          maxY: Math.max(0, ...yValues) + pad,
        };
        if (gridSize.maxX - gridSize.minX < 5) {
          const centerX = (gridSize.minX + gridSize.maxX) / 2;
          gridSize.minX = centerX - 5;
          gridSize.maxX = centerX + 5;
        }
        if (gridSize.maxY - gridSize.minY < 5) {
          const centerY = (gridSize.minY + gridSize.maxY) / 2;
          gridSize.minY = centerY - 5;
          gridSize.maxY = centerY + 5;
        }
      }

      const lines: Array<{ from: string; to: string; color?: string; style?: 'solid' | 'dashed' }> = [];
      if (coordConfig.lines && Array.isArray(coordConfig.lines)) {
        lines.push(...(coordConfig.lines as typeof lines));
      }

      const coordinatePlaneConfig = {
        gridSize,
        points,
        lines: lines.length > 0 ? lines : undefined,
        showGrid: (graphConfig as { showGrid?: boolean }).showGrid !== false,
        showAxisLabels: coordConfig.showAxisLabels !== false,
        showNumberLabels: coordConfig.showNumberLabels !== false,
        showCoordinateLines: coordConfig.showCoordinateLines !== false,
        title: (graphConfig as { title?: string }).title,
        description: coordConfig.description as string | undefined,
      };

      return <CoordinatePlane config={coordinatePlaneConfig} />;
    }
    default:
      return question.graph ? <GraphRenderer graphData={question.graph} /> : null;
  }
}

export interface MathQuestionBlockProps {
  question: Question;
  index: number;
}

const MathQuestionBlock: React.FC<MathQuestionBlockProps> = ({ question, index }) => (
  <Box
    key={index}
    className="question-block"
    sx={{
      mb: 4,
      pb: 3,
      borderBottom: '1px solid #e0e0e0',
      '& .katex': { fontSize: '1.1em' },
    }}
  >
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        color: '#9c27b0',
        mb: 2,
        pb: 1,
        borderBottom: '1px solid #e0e0e0',
        fontSize: '1.1rem',
      }}
    >
      Question {index + 1}
    </Typography>

    <Box sx={{ mb: 2, lineHeight: 1.8 }}>
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
        {question.question}
      </ReactMarkdown>
    </Box>

    {question.graph && (
      <Box sx={{ my: 1, maxWidth: '640px', mx: 'auto' }}>
        <QuestionVisualization question={question} />
      </Box>
    )}

    {question.answers && question.answers.length > 0 && (
      <Box sx={{ ml: 2, mt: 2, mb: 2 }}>
        {question.answers.map((answer, ansIndex) => {
          let answerText = isAnswerObject(answer) ? answer.text : answer;
          answerText = answerText.replace(/^[A-Da-d][\.\)\:\s]+\s*/, '');
          const letter = String.fromCharCode(65 + ansIndex);
          return (
            <Box key={ansIndex} sx={{ display: 'flex', mb: 1.5, alignItems: 'flex-start' }}>
              <Typography
                component="span"
                sx={{
                  fontWeight: 700,
                  color: '#7b1fa2',
                  minWidth: '28px',
                  mr: 1,
                }}
              >
                {letter}.
              </Typography>
              <Box sx={{ flex: 1, '& p': { m: 0 } }}>
                <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                  {answerText}
                </ReactMarkdown>
              </Box>
            </Box>
          );
        })}
      </Box>
    )}
  </Box>
);

export default MathQuestionBlock;
